import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { connect } from "react-redux";
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Button, TablePagination, TextField, InputAdornment, Chip, Tooltip, LinearProgress } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import PublishIcon from '@material-ui/icons/Publish';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import BlockIcon from '@material-ui/icons/Block';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { setSnackInfo } from '../../redux/actions/appAction';
import HttpService from '../../services/httpService';
import BomUploadDialog from './bomUploadDialog';

const NAVY = "#1C375D";
const CREAM = "#FFF2EB";
const GREEN = "#2E7D32";
const CARD_BORDER = "#E3E9F2";
const BRAND = "#F47D44";
const BRAND_SOFT = "#FFF4EC";
const BRAND_BORDER = "#F7D5C1";

// One visual treatment per BOM state returned by the API. Every pairing below clears WCAG AA
// (4.5:1) against its own fill, so the status is readable on any screen.
const BOM_STATE_META = {
	approved: { label: "Approved", icon: CheckCircleIcon, color: "#1B5E20", background: "#DFF3E2", border: "#A8D8AE" },
	pending: { label: "Pending", icon: HourglassEmptyIcon, color: "#8A6400", background: "#FFF1CE", border: "#EBD08A" },
	rejected: { label: "Rejected", icon: CancelIcon, color: "#B3261E", background: "#FBE2E1", border: "#F0B7B4" },
	draft: { label: "Draft", icon: CloudUploadIcon, color: "#2B4A80", background: "#E4ECFA", border: "#BBCEEC" },
	skipped: { label: "N/A", icon: BlockIcon, color: "#455571", background: "#E7ECF4", border: "#C6D1E2" },
	// A column this optimisation does not use at all - not a project level decision like skipped.
	disabled: { label: "N/A", icon: BlockIcon, color: "#455571", background: "#E7ECF4", border: "#C6D1E2" },
	notUploaded: { label: "Not Uploaded", icon: CancelIcon, color: "#C62828", background: "#FCE4E4", border: "#F2B8B8" }
};
const bomStateMeta = (bomState) => BOM_STATE_META[bomState?.state] || BOM_STATE_META.notUploaded;
const CARD_RADIUS = 14;
// The panels and the column header share one grid, so the columns line up down the page.
const GRID_MIN_WIDTH = 1060;
const ROW_INSET = 16;
const ACCENT_WIDTH = 4;
// Cells carry the gutter themselves, so the rules between them run edge to edge.
const CELL_PAD_X = 12;
const CELL_PAD_Y = 8;
const BOX_HEIGHT = 44;
const GRID_LINE = "#BFCEE2";
const GRID_LINE_HEAD = "rgba(255,242,235,0.38)";

const useStyles = makeStyles((theme) => ({
	page: { padding: theme.spacing(3), backgroundColor: "#F4F6FA", minHeight: "100vh" },
	hero: {
		background: `linear-gradient(120deg, ${NAVY} 0%, #2A4E80 100%)`,
		borderRadius: 16,
		padding: theme.spacing(3),
		marginBottom: theme.spacing(3),
		display: "flex",
		flexWrap: "wrap",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		boxShadow: "0 10px 30px rgba(28,55,93,0.25)"
	},
	heroTitle: { color: CREAM, margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: 0.3 },
	heroSubtitle: { color: "rgba(255,242,235,0.75)", margin: 0, marginTop: 6, fontSize: 14, maxWidth: 620 },
	heroStats: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(2), flexWrap: "wrap" },
	heroChip: { backgroundColor: "rgba(255,255,255,0.14)", color: CREAM, fontWeight: 600, border: "1px solid rgba(255,255,255,0.22)" },
	search: {
		backgroundColor: "#FFFFFF",
		borderRadius: 10,
		width: 300,
		"& .MuiOutlinedInput-root": { borderRadius: 10 },
		"& .MuiOutlinedInput-notchedOutline": { border: "none" }
	},
	heroActions: { display: "flex", alignItems: "center", flexWrap: "wrap" },
	refreshButton: {
		height: 40,
		// Explicit margin rather than a flex gap - the gap property is unreliable on older browsers here.
		marginLeft: theme.spacing(2),
		padding: "0 16px",
		borderRadius: 10,
		textTransform: "none",
		fontWeight: 600,
		color: CREAM,
		backgroundColor: "rgba(255,255,255,0.14)",
		border: "1px solid rgba(255,255,255,0.22)",
		"&:hover": { backgroundColor: "rgba(255,255,255,0.24)" },
		"&.Mui-disabled": { color: "rgba(255,242,235,0.5)", borderColor: "rgba(255,255,255,0.12)" }
	},
	"@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
	spinning: { animation: "$spin 900ms linear infinite" },

	// The bar sits above the list, so it appears without shifting the panels.
	refreshBar: { height: 3, borderRadius: 3, backgroundColor: "transparent", "& .MuiLinearProgress-bar": { backgroundColor: NAVY } },
	refreshBarSlot: { height: 3, marginBottom: theme.spacing(1) },

	// Narrow screens scroll the whole list sideways, never just one panel.
	listScroll: { overflowX: "auto", paddingBottom: 2 },
	list: { minWidth: GRID_MIN_WIDTH },

	// Same box model as a panel, so the labels sit exactly over the boxes below.
	listHeader: {
		display: "grid",
		alignItems: "stretch",
		borderLeft: `${ACCENT_WIDTH}px solid transparent`,
		borderRadius: 12,
		overflow: "hidden",
		background: `linear-gradient(180deg, #24456F 0%, ${NAVY} 100%)`,
		color: CREAM,
		boxShadow: "0 4px 14px rgba(28,55,93,0.18)",
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: 0.8,
		textTransform: "uppercase",
		textAlign: "center",
		marginBottom: theme.spacing(1.5)
	},
	listHeaderCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: `14px ${CELL_PAD_X}px`,
		borderRight: `1px solid ${GRID_LINE_HEAD}`,
		"&:last-child": { borderRight: "none" }
	},

	"@keyframes cardFadeIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "none" } },
	card: {
		backgroundColor: "#FFFFFF",
		border: `1px solid ${GRID_LINE}`,
		borderLeft: `${ACCENT_WIDTH}px solid ${BRAND}`,
		borderRadius: CARD_RADIUS,
		marginBottom: theme.spacing(2),
		boxShadow: "0 2px 10px rgba(28,55,93,0.06)",
		overflow: "hidden",
		transition: "box-shadow 180ms ease, transform 180ms ease",
		animation: "$cardFadeIn 260ms ease both",
		"&:hover": { boxShadow: "0 10px 26px rgba(28,55,93,0.12)", transform: "translateY(-1px)" }
	},
	cardReady: { borderLeftColor: GREEN, backgroundColor: "#FBFEFB" },

	cardHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		flexWrap: "wrap",
		padding: `14px ${ROW_INSET}px`,
		borderBottom: `1px solid ${BRAND_BORDER}`,
		background: `linear-gradient(180deg, #FFF9F5 0%, ${BRAND_SOFT} 100%)`
	},
	cardHeadReady: {
		background: "linear-gradient(180deg, #F6FCF7 0%, #EBF7EC 100%)",
		borderBottomColor: "#CFE8D1"
	},
	cardHeadMain: { display: "flex", alignItems: "center", minWidth: 0 },
	cardHeadRight: { display: "flex", alignItems: "center" },

	// How close this optimisation is to being pushable, without counting badges by eye.
	progress: { width: 170, marginRight: theme.spacing(2) },
	progressLabel: { fontSize: 11, fontWeight: 700, color: "#7A879B", marginBottom: 5, textAlign: "right", letterSpacing: 0.2 },
	progressTrack: { height: 6, borderRadius: 3, backgroundColor: "rgba(28,55,93,0.10)", overflow: "hidden" },
	progressFill: { height: "100%", borderRadius: 3, transition: "width 320ms ease" },
	slNo: {
		width: 36,
		height: 36,
		borderRadius: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#FFFFFF",
		border: `1px solid ${BRAND_BORDER}`,
		color: BRAND,
		fontWeight: 700,
		fontSize: 15,
		marginRight: theme.spacing(1.5)
	},
	slNoReady: { borderColor: "#C8E4CA", color: GREEN },
	optIdBox: {
		display: "flex",
		alignItems: "center",
		// A touch taller than the 44px status boxes below, so the heading leads the panel.
		minHeight: 50,
		padding: "8px 16px",
		borderRadius: 12,
		backgroundColor: "#FFFFFF",
		border: `1px solid ${BRAND_BORDER}`
	},
	optIdBoxReady: { borderColor: "#C8E4CA" },
	optId: { fontFamily: "'Roboto Mono', monospace", fontWeight: 700, fontSize: 19, color: BRAND, letterSpacing: 0.5 },
	optIdReady: { color: GREEN },
	statusChip: { height: 26, fontSize: 12, fontWeight: 700, marginLeft: theme.spacing(1.5) },
	statusReady: { backgroundColor: "#DDF0DE", color: GREEN, border: "1px solid #BFE0C1" },
	statusPending: { backgroundColor: "#FFEADF", color: BRAND, border: `1px solid ${BRAND_BORDER}` },
	projectCount: { fontSize: 13, fontWeight: 600, color: "#8A97AB", marginLeft: theme.spacing(2) },

	cardBody: { padding: 0 },
	projectRow: {
		display: "grid",
		alignItems: "stretch",
		borderBottom: `1px solid ${GRID_LINE}`,
		transition: "background-color 120ms ease",
		"&:last-child": { borderBottom: "none" },
		"&:hover": { backgroundColor: "#F6FAFF" },
		"&:hover $projectBox": { backgroundColor: "#E4EDFA", borderColor: "#C9DBF1" }
	},
	// The tooltip needs a real element to hold its ref, so it must not collapse the button.
	cellFill: { display: "block", width: "100%" },
	// The rule belongs to the cell, not the box, so it spans the whole row height.
	cell: {
		display: "flex",
		alignItems: "center",
		padding: `${CELL_PAD_Y}px ${CELL_PAD_X}px`,
		borderRight: `1px solid ${GRID_LINE}`,
		minWidth: 0,
		"&:last-child": { borderRight: "none" }
	},

	cellBox: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: BOX_HEIGHT,
		borderRadius: 10,
		border: "1px solid #E1E9F4",
		backgroundColor: "#F7FAFD",
		transition: "background-color 120ms ease, border-color 120ms ease"
	},
	projectBox: {
		fontFamily: "'Roboto Mono', monospace",
		fontSize: 14,
		fontWeight: 700,
		color: NAVY,
		backgroundColor: "#EDF3FC",
		borderColor: "#CFDDF2"
	},
	projectBoxMissing: { color: "#B3261E", backgroundColor: "#FBE2E1", borderColor: "#F0B7B4" },

	bomIcon: { fontSize: 18, marginRight: 6 },
	// A read-only column is not an entry point to anything, so it must not look clickable.
	bomBadge: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: "100%",
		height: BOX_HEIGHT,
		borderRadius: 10,
		padding: "0 8px",
		border: "1px solid #E1E9F4",
		fontSize: 14,
		fontWeight: 700,
		letterSpacing: 0.2,
		lineHeight: 1.2,
		textAlign: "center",
		boxSizing: "border-box"
	},
	// Each BOM cell is the entry point to that project's upload popup.
	bomButton: {
		width: "100%",
		minWidth: 0,
		height: BOX_HEIGHT,
		borderRadius: 10,
		padding: "0 8px",
		textTransform: "none",
		fontSize: 14,
		fontWeight: 700,
		letterSpacing: 0.2,
		lineHeight: 1.2,
		border: "1px solid #E1E9F4",
		transition: "box-shadow 120ms ease, transform 120ms ease",
		"&:hover": { boxShadow: "0 4px 12px rgba(28,55,93,0.14)", transform: "translateY(-1px)" }
	},

	pushButton: {
		background: `linear-gradient(120deg, ${GREEN} 0%, #45A049 100%)`,
		color: "#FFFFFF",
		fontWeight: 700,
		fontSize: 14,
		minHeight: 50,
		borderRadius: 12,
		textTransform: "none",
		padding: "12px 22px",
		boxShadow: "0 4px 12px rgba(46,125,50,0.28)",
		"&:hover": { background: `linear-gradient(120deg, #256428 0%, ${GREEN} 100%)` },
		"&.Mui-disabled": {
			background: "rgba(255,255,255,0.7)",
			color: "#A9B6C8",
			border: `1px solid ${CARD_BORDER}`,
			boxShadow: "none"
		}
	},

	noProjects: {
		padding: `10px ${ROW_INSET}px`,
		color: "#8A97AB",
		fontStyle: "italic",
		fontSize: 13
	},
	skeletonBox: { borderRadius: 10, height: BOX_HEIGHT, width: "100%", transform: "none" },

	emptyState: {
		textAlign: "center",
		padding: theme.spacing(6),
		color: "#8A97AB",
		backgroundColor: "#FFFFFF",
		border: `1px solid ${CARD_BORDER}`,
		borderRadius: CARD_RADIUS
	},
	emptyIcon: { fontSize: 46, color: "#C4CDDB", marginBottom: 8 },

	pagination: { marginTop: theme.spacing(1), borderRadius: 12, border: `1px solid ${CARD_BORDER}` }
}));

const PushToProd = (props) => {
	const classes = useStyles();
	const [bomTypes, setBomTypes] = useState([])
	const [optimisations, setOptimisations] = useState([])
	const [searchField, setSearchField] = useState("")
	const [page, setPage] = useState(1)
	const [rowsPerPage] = useState(10)
	const [totalCount, setTotalCount] = useState(0)
	const [initialLoading, setInitialLoading] = useState(true)
	const [refreshing, setRefreshing] = useState(false)
	// The BOM cell that opened the upload popup: { project, bomType, status }.
	const [activeBomCell, setActiveBomCell] = useState(null)
	const [bomDialogOpen, setBomDialogOpen] = useState(false)

	const { setSnackInfo } = props
	const searchTimer = useRef(null)

	useEffect(() => () => clearTimeout(searchTimer.current), [])
	const getPushToProdList = useCallback(async (pageNumber = 1, search = "", isInitial = false) => {
		if (isInitial) setInitialLoading(true)
		else setRefreshing(true)
		try {
			const response = await HttpService.getPushToProdList(pageNumber, rowsPerPage, search)
			setBomTypes(response.data?.bomTypes || [])
			setOptimisations(response.data?.data || [])
			setTotalCount(response.data?.totalCount || 0)
		} catch (error) {
			setSnackInfo(error?.response?.data?.message ?? "Something went wrong", "error")
			setOptimisations([])
			setTotalCount(0)
		}
		setInitialLoading(false)
		setRefreshing(false)
	}, [rowsPerPage, setSnackInfo])

	useEffect(() => {
		getPushToProdList(1, "", true)
	}, [getPushToProdList])

	const handlePageChange = (event, newPage) => {
		const pageNumber = newPage + 1
		setPage(pageNumber)
		getPushToProdList(pageNumber, searchField)
	}

	const searchOptimisation = (e) => {
		const value = e.target.value
		setSearchField(value)
		setPage(1)
		clearTimeout(searchTimer.current)
		searchTimer.current = setTimeout(() => getPushToProdList(1, value), 400)
	}

	// Refetch the page the user is looking at, keeping the active search.
	const refreshList = () => {
		clearTimeout(searchTimer.current)
		getPushToProdList(page, searchField)
	}

	const openBomDialog = useCallback((project, bomType) => {
		setActiveBomCell({ project, bomType, status: project.bomStatus?.[bomType.key] })
		setBomDialogOpen(true)
	}, [])

	// Closing only starts the fade - tearing the popup down and refetching the list both wait
	// for the exit to finish, so the click itself stays cheap.
	const closeBomDialog = () => setBomDialogOpen(false)

	// The popup can upload, submit or skip a BOM, so always refetch on the way out.
	const bomDialogExited = () => {
		setActiveBomCell(null)
		refreshList()
	}

	// These feed the memoised panels below, so they stay stable across popup open and close.
	const pushToProduction = useCallback((optimisation) => {
		setSnackInfo(`Push to production for ${optimisation.optimizationNumber} is not enabled yet`, "info")
	}, [setSnackInfo])
	const progressOf = useCallback((optimisation) => {
		const projects = optimisation.projects || []
		const total = projects.length * bomTypes.length
		const done = projects.reduce(
			(count, project) => count + bomTypes.filter(bomType => project.bomStatus?.[bomType.key]?.satisfied).length,
			0
		)
		return { done, total, pending: total - done, percent: total ? Math.round((done / total) * 100) : 0 }
	}, [bomTypes])

	const SKELETON_COLUMNS = 5
	// One grid for the header and every project row, so the columns stay aligned across panels.
	const columnCount = bomTypes.length || SKELETON_COLUMNS
	const gridTemplate = useMemo(() => ({
		gridTemplateColumns: `minmax(150px, 1.1fr) repeat(${columnCount}, minmax(145px, 1fr))`
	}), [columnCount])

	const renderSkeletonCards = () => [3, 2, 4].map((projectCount, cardIndex) => (
		<div key={`skeleton-${cardIndex}`} className={classes.card}>
			<div className={classes.cardHead}>
				<div className={classes.cardHeadMain}>
					<Skeleton variant="rect" width={30} height={30} style={{ borderRadius: 8, marginRight: 12 }} />
					<Skeleton variant="rect" width={150} height={34} style={{ borderRadius: 10 }} />
				</div>
				<Skeleton variant="rect" width={168} height={40} className={classes.skeletonBox} />
			</div>
			<div className={classes.cardBody}>
				{Array.from({ length: projectCount }).map((unused, projectIndex) => (
					<div key={projectIndex} className={classes.projectRow} style={gridTemplate}>
						{Array.from({ length: columnCount + 1 }).map((cell, columnIndex) => (
							<div key={columnIndex} className={classes.cell}>
								<Skeleton variant="rect" className={classes.skeletonBox} />
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	))

	const readyOnPage = optimisations.filter(optimisation => optimisation.isReadyToPush).length

	const renderProjectRow = useCallback((project) => (
		<div key={project.projectId} className={classes.projectRow} style={gridTemplate}>
			<div className={classes.cell}>
				<div className={`${classes.cellBox} ${classes.projectBox} ${project.projectFound === false ? classes.projectBoxMissing : ""}`}>
					{project.projectNumber}
				</div>
			</div>
			{bomTypes.map(bomType => {
				const bomState = project.bomStatus?.[bomType.key]
				const meta = bomStateMeta(bomState)
				const StateIcon = meta.icon
				const stageLabel = bomState?.label || meta.label
				const missingProject = project.projectFound === false
				// The server turns a column off when this optimisation has no use for it, such as
				// the Modular Custom BOM on an optimisation with a single tagged project.
				const columnDisabled = Boolean(bomState?.disabled)
				// Modular BOM is normally filled in by the optimisation upload, but a single tagged
				// project has no optimisation driving it, so the server hands the column over.
				const readOnly = bomState?.readOnly ?? bomType.readOnly

				// A read-only column is filled in by the optimisation upload and a disabled one is
				// not in play at all, so neither opens anything - the cell only reports the stage.
				if (readOnly || columnDisabled) {
					const badgeTooltip = missingProject
						? "This project could not be found"
						: (bomState?.hint || `${bomType.label}: ${stageLabel}`)

					return (
						<div key={bomType.key} className={classes.cell}>
							<Tooltip title={badgeTooltip}>
								<div
									className={classes.bomBadge}
									style={{ backgroundColor: meta.background, borderColor: meta.border, color: meta.color }}
								>
									<StateIcon className={classes.bomIcon} style={{ color: meta.color }} />
									{stageLabel}
								</div>
							</Tooltip>
						</div>
					)
				}

				const tooltip = missingProject
					? "This project could not be found"
					: `${bomType.label}: ${stageLabel}. Click to upload or update it.`

				return (
					<div key={bomType.key} className={classes.cell}>
						<Tooltip title={tooltip}>
							<span className={classes.cellFill}>
								<Button
									className={classes.bomButton}
									style={{ backgroundColor: meta.background, borderColor: meta.border, color: meta.color }}
									disabled={missingProject}
									onClick={() => openBomDialog(project, bomType)}
								>
									<StateIcon className={classes.bomIcon} style={{ color: meta.color }} />
									{stageLabel}
								</Button>
							</span>
						</Tooltip>
					</div>
				)
			})}
		</div>
	), [classes, bomTypes, openBomDialog, gridTemplate])

	// Opening or closing the popup must not repaint every panel on the page, so they are
	// rebuilt only when the listing itself changes.
	const optimisationCards = useMemo(() => optimisations.map(optimisation => {
		const projects = optimisation.projects || []
		const progress = progressOf(optimisation)
		const pendingCount = progress.pending
		const ready = Boolean(optimisation.isReadyToPush)

		return (
			<div key={optimisation._id} className={`${classes.card} ${ready ? classes.cardReady : ""}`}>
				<div className={`${classes.cardHead} ${ready ? classes.cardHeadReady : ""}`}>
					<div className={classes.cardHeadMain}>
						<div className={`${classes.slNo} ${ready ? classes.slNoReady : ""}`}>{optimisation.slNo}</div>
						<div className={`${classes.optIdBox} ${ready ? classes.optIdBoxReady : ""}`}>
							<span className={`${classes.optId} ${ready ? classes.optIdReady : ""}`}>{optimisation.optimizationNumber}</span>
							<Chip
								size="small"
								className={`${classes.statusChip} ${ready ? classes.statusReady : classes.statusPending}`}
								icon={ready
									? <DoneAllIcon style={{ fontSize: 15, color: GREEN }} />
									: <HourglassEmptyIcon style={{ fontSize: 15, color: BRAND }} />}
								label={ready ? "Ready" : "Pending"}
							/>
						</div>
						<span className={classes.projectCount}>
							{projects.length} project{projects.length === 1 ? "" : "s"} tagged
						</span>
					</div>

					<div className={classes.cardHeadRight}>
						{progress.total > 0 && (
							<div className={classes.progress}>
								<div className={classes.progressLabel}>
									{progress.done} of {progress.total} BOMs approved
								</div>
								<div className={classes.progressTrack}>
									<div
										className={classes.progressFill}
										style={{ width: `${progress.percent}%`, backgroundColor: ready ? GREEN : BRAND }}
									/>
								</div>
							</div>
						)}

						<Tooltip
							title={ready
								? "All BOMs approved. Ready to push."
								: `${pendingCount} BOM approval${pendingCount === 1 ? "" : "s"} pending`}
						>
							<span>
								<Button
									className={classes.pushButton}
									variant="contained"
									disableElevation
									startIcon={<PublishIcon />}
									disabled={!ready}
									onClick={() => pushToProduction(optimisation)}
								>
									Push To Production
								</Button>
							</span>
						</Tooltip>
					</div>
				</div>

				<div className={classes.cardBody}>
					{projects.length
						? projects.map(project => renderProjectRow(project))
						: <div className={classes.noProjects}>No projects tagged</div>}
				</div>
			</div>
		)
	}), [optimisations, classes, renderProjectRow, progressOf, pushToProduction])

	return (
		<div className={classes.page}>
			<div className={classes.hero}>
				<div>
					<h1 className={classes.heroTitle}>Push To Production</h1>
					<p className={classes.heroSubtitle}>
						BOM approval status for every tagged project. An optimization can be pushed once all of its BOMs are approved.
					</p>
					<div className={classes.heroStats}>
						<Chip
							size="small"
							className={classes.heroChip}
							label={initialLoading ? "Loading optimizations" : `${totalCount} optimization${totalCount === 1 ? "" : "s"}`}
						/>
						{!initialLoading && (
							<Chip size="small" className={classes.heroChip} icon={<DoneAllIcon style={{ color: "#9BE6A0" }} />} label={`${readyOnPage} ready on this page`} />
						)}
					</div>
				</div>

				<div className={classes.heroActions}>
					<TextField
						className={classes.search}
						placeholder="Search optimization number"
						type="text"
						value={searchField}
						name="searchField"
						onChange={searchOptimisation}
						variant="outlined"
						size="small"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon style={{ color: "#8A97AB" }} />
								</InputAdornment>
							)
						}}
					/>

					<Tooltip title="Refresh BOM approval status">
						<span>
							<Button
								className={classes.refreshButton}
								onClick={refreshList}
								disabled={initialLoading || refreshing}
								startIcon={<RefreshIcon className={refreshing ? classes.spinning : ""} />}
							>
								{refreshing ? "Refreshing" : "Refresh"}
							</Button>
						</span>
					</Tooltip>
				</div>
			</div>

			<div className={classes.refreshBarSlot}>
				{refreshing && <LinearProgress className={classes.refreshBar} />}
			</div>

			<div className={classes.listScroll} style={{ opacity: refreshing ? 0.55 : 1, transition: "opacity 180ms ease" }}>
				<div className={classes.list}>
					<div className={classes.listHeader} style={gridTemplate}>
						<span className={classes.listHeaderCell}>Tagged Project IDs</span>
						{initialLoading && bomTypes.length === 0
							? Array.from({ length: SKELETON_COLUMNS }).map((column, columnIndex) => (
								<span key={columnIndex} className={classes.listHeaderCell}>
									<Skeleton variant="text" width={80} style={{ backgroundColor: "rgba(255,242,235,0.25)" }} />
								</span>
							))
							: bomTypes.map(bomType => <span key={bomType.key} className={classes.listHeaderCell}>{bomType.label}</span>)}
					</div>

					{initialLoading && renderSkeletonCards()}

					{!initialLoading && optimisations.length === 0 && (
						<div className={classes.emptyState}>
							<ErrorOutlineIcon className={classes.emptyIcon} />
							<div style={{ fontSize: 15, fontWeight: 600 }}>No optimizations found</div>
							<div style={{ fontSize: 13, marginTop: 4 }}>
								{searchField ? `Nothing matches "${searchField}".` : "Auto numbered optimizations will appear here."}
							</div>
						</div>
					)}

					{!initialLoading && optimisationCards}
				</div>
			</div>

			<BomUploadDialog
				open={bomDialogOpen}
				project={activeBomCell?.project}
				bomType={activeBomCell?.bomType}
				status={activeBomCell?.status}
				onClose={closeBomDialog}
				onExited={bomDialogExited}
			/>

			<Paper className={classes.pagination} elevation={0}>
				<TablePagination
					rowsPerPageOptions={[10]}
					component="div"
					count={totalCount}
					rowsPerPage={rowsPerPage}
					page={page - 1}
					onPageChange={handlePageChange}
				/>
			</Paper>
		</div>
	)
}

export default connect(null, { setSnackInfo })(PushToProd)
